import { useEffect, useState } from 'react';

const DEBUG_KEY = 'ATTACH_DEBUG';

const getLocalStorage = () => {
    try {
        return window?.localStorage;
    } catch (error) {
        return null;
    }
};

export const isAttachDebugEnabled = () => {
    const storage = getLocalStorage();
    if (!storage) return false;
    return storage.getItem(DEBUG_KEY) === '1';
};

const subscribers = new Set();

const debugState = {
    enabled: false,
    connected: false,
    lastDirection: null,
    lastEvent: null,
    lastPayload: null,
    lastAttachmentCommand: null,
};

const notify = () => {
    const snapshot = {
        ...debugState,
        enabled: isAttachDebugEnabled(),
    };
    subscribers.forEach((fn) => fn(snapshot));
};

export const subscribeSocketDebug = (fn) => {
    subscribers.add(fn);
    fn({
        ...debugState,
        enabled: isAttachDebugEnabled(),
    });
    return () => subscribers.delete(fn);
};

export const useSocketDebugState = () => {
    const [snapshot, setSnapshot] = useState({
        ...debugState,
        enabled: isAttachDebugEnabled(),
    });

    useEffect(() => subscribeSocketDebug(setSnapshot), []);

    return snapshot;
};

const logEvent = (direction, eventName, payload) => {
    if (!isAttachDebugEnabled()) return;
    debugState.enabled = true;
    debugState.lastDirection = direction;
    debugState.lastEvent = eventName;
    debugState.lastPayload = payload;
    if (direction === 'emit' && eventName?.startsWith('attachment:')) {
        debugState.lastAttachmentCommand = eventName;
    }
    if (direction === 'on' && eventName === 'disconnect') {
        debugState.connected = false;
    } else if (direction === 'on' && eventName === 'connect') {
        debugState.connected = true;
    }
    // eslint-disable-next-line no-console
    console.debug(`[socket][${direction}]`, eventName, ...(payload || []));
    notify();
};

export const setAttachmentCommandDebug = (eventName) => {
    if (!isAttachDebugEnabled()) return;
    debugState.lastAttachmentCommand = eventName;
    notify();
};

export const initSocketDebug = (socket) => {
    if (!socket || socket.__attachDebugPatched) return socket;

    debugState.connected = !!socket.connected;
    const originalEmit = socket.emit.bind(socket);
    socket.emit = (...args) => {
        const [eventName, ...payload] = args;
        const result = originalEmit(...args);
        logEvent('emit', eventName, payload);
        return result;
    };

    if (typeof socket.onAny === 'function') {
        const anyLogger = (eventName, ...payload) => logEvent('on', eventName, payload);
        socket.onAny(anyLogger);
    }

    const handleConnect = () => logEvent('on', 'connect', []);
    const handleDisconnect = (reason) => logEvent('on', 'disconnect', [reason]);
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);

    socket.__attachDebugPatched = true;
    return socket;
};

export default initSocketDebug;
